import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import RetailerLayout from "@/layouts/RetailerLayout";
import AdminLayout from "@/layouts/AdminLayout";
import RetailerDashboard from "@/pages/retailer/dashboard/page";
import RetailerKyc from "@/pages/retailer/kyc/page";
import RetailerCreateMandate from "@/pages/retailer/create-mandate/page";
import RetailerMandates from "@/pages/retailer/mandates/page";
import RetailerMandateDetail from "@/pages/retailer/mandates/detail-page";
import RetailerInstallments from "@/pages/retailer/installments/page";
import RetailerWallet from "@/pages/retailer/wallet/page";
import ProfilePage from "@/pages/profile/page";
import AdminDashboard from "@/pages/admin/dashboard/page";
import AdminMandates from "@/pages/admin/mandates/page";
import AdminMandateDetail from "@/pages/admin/mandates/detail-page";
import AdminRetailers from "@/pages/admin/retailers/page";
import AdminRetailerDetail from "@/pages/admin/retailers/detail-page";
import AdminWallets from "@/pages/admin/wallets/page";
import AdminWalletLedger from "@/pages/admin/wallets/ledger-page";
import AdminInstallments from "@/pages/admin/installments/page";
import AdminKyc from "@/pages/admin/kyc/page";
import AdminKycReview from "@/pages/admin/kyc/review-page";
import AdminLogs from "@/pages/admin/logs/page";
import AdminReports from "@/pages/admin/reports/page";
import AdminSettings from "@/pages/admin/settings/page";
import { NotFoundPage } from "@/components/ui/ErrorPage";
import MockAuthPage from "@/pages/mock-auth/page";
import MockAuthStatusPage from "@/pages/mock-auth/status-page";
import RoutePermissionGuard from "@/components/feature/auth/RoutePermissionGuard";
import { getSession, isStaffSession } from "@/components/feature/auth/session";
import { usePermission } from "@/context/PermissionContext";

function RootRedirect() {
  const session = getSession();
  if (!session) {
    return <Navigate to="/login" replace />;
  }
  if (isStaffSession(session)) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/retailer" replace />;
}

function AdminIndexRedirect() {
  const { canView, isSuperAdmin } = usePermission();
  if (isSuperAdmin || canView("dashboard")) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  const modules = ["mandates", "installments", "retailers", "wallets", "logs", "reports", "settings"];
  const first = modules.find((m) => canView(m));
  if (first) {
    return <Navigate to={`/admin/${first}`} replace />;
  }
  return <Navigate to="/admin/profile" replace />;
}

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/register",
    element: <Register />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/auth/:refNo/status",
    element: <MockAuthStatusPage />,
  },
  {
    path: "/auth/:refNo",
    element: <MockAuthPage />,
  },
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/retailer",
    element: <RetailerLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/retailer/dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <RetailerDashboard />,
      },
      {
        path: "kyc",
        element: <RetailerKyc />,
      },
      {
        path: "create-mandate",
        element: <RetailerCreateMandate />,
      },
      {
        path: "mandates",
        element: <RetailerMandates />,
      },
      {
        path: "mandates/:refNo",
        element: <RetailerMandateDetail />,
      },
      {
        path: "installments",
        element: <RetailerInstallments />,
      },
      {
        path: "wallet",
        element: <RetailerWallet />,
      },
      {
        path: "profile",
        element: <ProfilePage />,
      },
    ],
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <AdminIndexRedirect />,
      },
      {
        path: "dashboard",
        element: (
          <RoutePermissionGuard module="dashboard" moduleName="Dashboard">
            <AdminDashboard />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "mandates",
        element: (
          <RoutePermissionGuard module="mandates" moduleName="Mandates">
            <AdminMandates />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "mandates/:refNo",
        element: (
          <RoutePermissionGuard module="mandates" moduleName="Mandates">
            <AdminMandateDetail />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "retailers",
        element: (
          <RoutePermissionGuard module="retailers" moduleName="Retailers">
            <AdminRetailers />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "retailers/:id",
        element: (
          <RoutePermissionGuard module="retailers" moduleName="Retailers">
            <AdminRetailerDetail />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "kyc",
        element: (
          <RoutePermissionGuard module="kyc" moduleName="KYC Approvals">
            <AdminKyc />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "kyc/:retailerId",
        element: (
          <RoutePermissionGuard module="kyc" moduleName="KYC Approvals">
            <AdminKycReview />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "wallets",
        element: (
          <RoutePermissionGuard module="wallets" moduleName="Wallets">
            <AdminWallets />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "wallets/:retailerId",
        element: (
          <RoutePermissionGuard module="wallets" moduleName="Wallets">
            <AdminWalletLedger />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "installments",
        element: (
          <RoutePermissionGuard module="installments" moduleName="Installments">
            <AdminInstallments />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "reports",
        element: (
          <RoutePermissionGuard module="reports" moduleName="Reports">
            <AdminReports />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "settings",
        element: (
          <RoutePermissionGuard module="settings" moduleName="Settings">
            <AdminSettings />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "profile",
        element: <ProfilePage />,
      },
      {
        path: "logs",
        element: (
          <RoutePermissionGuard module="logs" moduleName="Audit Logs">
            <AdminLogs />
          </RoutePermissionGuard>
        ),
      },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

export default routes;